import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Film, AlertCircle, Eye } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { MLText } from '../components/MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedBreakdownView = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSharedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchSharedData = async () => {
    try {
      const response = await axios.get(`${API}/share/${token}`);
      setData(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to load shared breakdown');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      PROP: 'bg-blue-500/20 text-blue-400',
      COSTUME: 'bg-yellow-500/20 text-yellow-400',
      ART_SET_DRESSING: 'bg-purple-500/20 text-purple-400',
      VFX: 'bg-cyan-500/20 text-cyan-400',
      STUNT: 'bg-orange-500/20 text-orange-400',
      SFX_SPECIAL_EFFECTS: 'bg-red-500/20 text-red-400',
      MAKEUP_HAIR: 'bg-pink-500/20 text-pink-400',
      SOUND_MUSIC: 'bg-teal-500/20 text-teal-400',
      VEHICLE: 'bg-amber-500/20 text-amber-400',
      ANIMAL: 'bg-green-500/20 text-green-400',
      EXTRA_BACKGROUND: 'bg-lime-500/20 text-lime-400',
    };
    return colors[category] || 'bg-gray-500/20 text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
        <div className="text-[#a0a0a0]">Loading shared breakdown...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center p-4">
        <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center max-w-md">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h3 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0] mb-2">Unable to Load</h3>
          <p className="text-[#a0a0a0]">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d]" data-testid="shared-breakdown-page">
      <div className="border-b border-[#2a2a2a] bg-[#0a0a0a] p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Film className="h-8 w-8 text-[#e8c547]" />
            <div>
              <span className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0]">FiPr.ai</span>
              <p className="text-xs text-[#a0a0a0]">Shared Production Breakdown</p>
            </div>
          </div>
          <Badge variant="outline" className="border-[#2a2a2a] text-[#a0a0a0]">
            <Eye className="h-3 w-3 mr-1" />
            View-only
          </Badge>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="font-['DM_Serif_Display'] text-4xl text-[#f0f0f0] mb-2">{data.project?.title}</h1>
          {data.project?.production_company && (
            <p className="text-[#a0a0a0]">{data.project.production_company}</p>
          )}
          {data.department_filter && (
            <Badge className="mt-3 bg-[#4a9eff]/20 text-[#4a9eff]">
              Filtered: {data.department_filter}
            </Badge>
          )}
        </div>

        <div className="space-y-4" data-testid="shared-scenes-list">
          {data.scenes?.map((scene) => (
            <Card key={scene.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-[#e8c547] font-bold text-lg">{formatSceneNumber(scene.scene_number, { prefix: '#' })}</span>
                    <h3 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0]">{scene.scene_header}</h3>
                  </div>
                  <p className="text-[#a0a0a0]">{scene.synopsis}</p>
                </div>
                <span className="text-xs text-[#a0a0a0] bg-[#0d0d0d] px-2 py-1 rounded">
                  {scene.page_count_eighths}/8 page
                </span>
              </div>

              {Object.entries(scene.breakdown_items || {}).map(([category, items]) => (
                items.length > 0 && (
                  <div key={category} className="mt-4 pt-4 border-t border-[#2a2a2a]">
                    <Badge className={getCategoryBadgeColor(category) + ' mb-2'}>
                      {category.replace(/_/g, ' ')}
                    </Badge>
                    <ul className="space-y-1 mt-2">
                      {items.map((item, idx) => (
                        <li key={idx} className="text-[#a0a0a0] text-sm pl-4">
                          {item.character_name && (
                            <span className="text-[#f0f0f0]">{item.character_name}: </span>
                          )}
                          <MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              ))}
            </Card>
          ))}
        </div>
      </div>

      <footer className="border-t border-[#2a2a2a] py-6 px-4 mt-12">
        <div className="max-w-7xl mx-auto text-center text-[#a0a0a0] text-sm">
          <p>Powered by FiPr.ai — AI-powered film production breakdown</p>
        </div>
      </footer>
    </div>
  );
};

export default SharedBreakdownView;
